import React, { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "doctodays_cookie_consent_v1";

/**
 * Lightweight cookie banner (no third-party tracking → info-only).
 *
 * We only set strictly-necessary cookies (auth tokens, session). GDPR/ePrivacy
 * still requires informing the user when the page first loads. We persist the
 * acknowledgement in localStorage so it's not shown again.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Defer a tick so it doesn't compete with page-load animations
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage disabled — skip */
    }
  }, []);

  const acknowledge = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: new Date().toISOString(), v: 1 }));
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[70] bg-white shadow-2xl rounded-2xl border border-slate-200 p-4 md:p-5"
      role="dialog"
      aria-label="Information cookies"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
          <Cookie className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 mb-1">
            Vos données, votre santé.
          </p>
          <p className="text-xs text-slate-600 leading-relaxed">
            DoctoDays n'utilise que des cookies <strong>strictement nécessaires</strong> au
            fonctionnement du service (authentification, session). Pas de pub, pas de
            traceurs tiers.{" "}
            <Link to="/privacy" className="text-emerald-600 hover:underline font-medium">
              En savoir plus
            </Link>
            .
          </p>
        </div>
        <button
          onClick={acknowledge}
          aria-label="Fermer"
          className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
          data-testid="cookie-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={acknowledge}
          className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
          data-testid="cookie-banner-accept"
        >
          J'ai compris
        </button>
      </div>
    </div>
  );
}
